import $ from '../../../../../FrontendBundle/Resources/js/jquery';
import SetItemAlignCommand from '../command/setItemAlignCommand';
import DropdownField from './dropdownField';

/**
 * @class AlignField
 */
export default class AlignField extends DropdownField {
    type = 'align';
    supports = null;

    getDropdownTemplateString() {
        return `
            <li data-item="<%= key %>"<% if (active) { %> class="active"<% } %>>
                <div class="thumbnail">
                    <img src="<%= image %>" width="50" height="50" />
                </div>
                <h3 class="title"><%= value %></h3>
            </li>
        `;
    }

    /**
     * Register click event on dropdown.
     *
     * @param {jQuery} dropdown
     */
    registerEventsForDropdown(dropdown) {
        dropdown.on('click', 'li', (...args) => this.onChangeAlignment(...args));
    }

    /**
     * Load all alignment items and replace the original items
     *
     * @param {Event} event
     */
    onChangeAlignment(event) {
        const target = $(event.currentTarget);
        const selection = this.getSelection();
        const canvas = this.editor.getCanvas();
        const history = canvas.getHistory();
        const item = selection.first();
        const alignments = this.getAlignmentsForSelection();

        const promises = alignments[target.data('item')]
            ?.map(alignment => this.loadReplacementItem(alignment));

        Promise.all(promises)
            .then((replacements) => {
                const command = new SetItemAlignCommand(
                    item.getParentItem().getItems(),
                    selection,
                    replacements,
                );

                history.addAndExecuteCommand(command);
                return null;
            })
            // eslint-disable-next-line no-console
            .catch(console.error);
    }

    async loadReplacementItem(alignment) {
        const { newItemId, newType, oldItem } = alignment;
        const canvas = this.editor.getCanvas();
        const itemFactory = canvas.getItemFactory();
        const newItem = await itemFactory.create(
            newType,
            { itemId: newItemId },
            oldItem.getParentItem(),
        );
        newItem.set('position', oldItem.get('position'));
        newItem.set('rotation', oldItem.get('rotation'));

        return { newItem, oldItem };
    }

    /**
     * Update text with item value.
     */
    updateText() {
        const selection = this.getSelection();
        const alignmentCount = {};
        let maxAlignment = '';

        selection.each((item) => {
            const alignment = item.get('align');

            if (!alignmentCount[alignment]) {
                alignmentCount[alignment] = 0;
            }

            alignmentCount[alignment] += 1;

            if (!maxAlignment || alignmentCount[alignment] > alignmentCount[maxAlignment]) {
                maxAlignment = alignment;
            }
        });

        if (Object.keys(alignmentCount).length > 1) {
            maxAlignment = `<i>${maxAlignment}, ...</i>`;
        }

        this.button.children('.text').html(maxAlignment);
    }

    /**
     * Update text with item value.
     */
    updateOptions() {
        const alignments = this.getAlignmentsForSelection();
        const options = [];

        Object.entries(alignments).forEach(([name, alignment]) => {
            options.push({
                key: name,
                value: name,
                active: false,
                image: alignment[0].image,
            });
        });

        this.setOptions(options);
    }

    /**
     * Update item with field value.
     */
    updateItem() {}

    /**
     * Do action.
     *
     * @param {ItemInterface} item
     * @param {Object} parameters
     */
    action(item, parameters) {
        if (!this.isHidden && parameters && parameters.showAlignment) {
            $('.dropdown-menu', this.control).dropdown('toggle');
        }
    }

    /**
     * Check if field is supportet by item.
     *
     * @return {boolean}
     */
    isSupported() {
        const selection = this.getSelection();
        let isSupported = true;

        if (selection.length < 1) {
            return false;
        }

        selection.each((item) => {
            isSupported = isSupported && item.supportsAlign;
        });

        const commonAlignments = this.numberOfCommonAlignments();
        const selectedAlignments = this.numberOfCurrentSelectedAlignments();
        const somethingToChange = commonAlignments !== 1 || selectedAlignments !== 1;

        isSupported = isSupported && commonAlignments > 0 && somethingToChange;

        return isSupported;
    }

    getSelection() {
        return this.editor.getCanvas().getSelection();
    }

    /**
     * Get a object with a key for every common alignment of the selected items and their corresponding information
     *
     * @return {Object}
     */
    getAlignmentsForSelection() {
        const productionDataStore = this.editor.getCanvas().getItemFactory().getProductionDataStore();
        let alignments = null;

        this.getSelection().each((item) => {
            const productFamily = productionDataStore.getProductFamilyForItem(item);
            const foundAlignments = [];

            if (productFamily) {
                const alignOptions = productFamily.getAlignOptions(item);

                if (alignments) {
                    alignOptions.forEach((align) => {
                        if (!alignments[align.value]) {
                            return;
                        }

                        foundAlignments.push(align.value);

                        alignments[align.value].push({
                            oldItem: item,
                            newItemId: align.key,
                            newType: align.type,
                            image: align.image,
                        });
                    });

                    Object.keys(alignments).forEach((key) => {
                        if (foundAlignments.indexOf(key) < 0) {
                            delete alignments[key];
                        }
                    });
                } else {
                    alignments = {};

                    alignOptions.forEach((align) => {
                        if (!alignments[align.value]) {
                            alignments[align.value] = [];
                        }

                        alignments[align.value].push({
                            oldItem: item,
                            newItemId: align.key,
                            newType: align.type,
                            image: align.image,
                        });
                    });
                }
            }
        });

        return alignments || {};
    }

    /**
     * Get the number of common alignments of all selected items
     *
     * @return {number}
     */
    numberOfCommonAlignments() {
        return Object.keys(this.getAlignmentsForSelection()).length;
    }

    /**
     * Get the number of current different alignments of all selected items
     *
     * @return {number}
     */
    numberOfCurrentSelectedAlignments() {
        const selection = this.getSelection();
        const alignments = [];

        selection.each((item) => {
            const alignment = item.get('align');
            if (alignments.indexOf(alignment) < 0) {
                alignments.push(alignment);
            }
        });

        return alignments.length;
    }
}
